/**
 * ToastProvider + useToast() — globales Melde-System.
 *
 * Ersetzt die per-Screen-Implementierungen (admin/Settings-Banner,
 * MandantDsgvoSelfService-StatusToast) durch einen konsistenten Stack unten
 * rechts. Bewusst selbstgebaut statt Library: minimaler Bedarf, kein neues
 * Supply-Chain-/DSGVO-Review, Editorial-Stil bleibt konsistent.
 *
 * A11y: success/info als role="status" (polite), error als role="alert"
 * (assertive) — Muster aus dem früheren StatusToast übernommen. Fehler
 * bleiben länger stehen (8 s statt 4 s) und jeder Toast ist manuell
 * schließbar.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { de } from '../../lib/i18n/de';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastApi {
  readonly show: (message: string, kind?: ToastKind) => void;
}

interface ToastItem {
  readonly id: number;
  readonly message: string;
  readonly kind: ToastKind;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast muss innerhalb von <ToastProvider> verwendet werden.');
  }
  return ctx;
}

const AUTO_DISMISS_MS: Record<ToastKind, number> = {
  success: 4000,
  info: 4000,
  error: 8000,
};

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastItem[]>([]);
  const nextIdRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, kind: ToastKind = 'info') => {
      const id = nextIdRef.current;
      nextIdRef.current += 1;
      setToasts((prev) => [...prev, { id, message, kind }]);
      window.setTimeout(() => {
        dismiss(id);
      }, AUTO_DISMISS_MS[kind]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-end gap-2 sm:inset-x-auto sm:right-4 sm:w-full sm:max-w-sm">
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONES: Record<ToastKind, string> = {
  success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  error: 'border-red-300 bg-red-50 text-red-900',
  info: 'border-stone-300 bg-stone-50 text-stone-900',
};

function ToastCard({
  toast,
  onDismiss,
}: {
  readonly toast: ToastItem;
  readonly onDismiss: (id: number) => void;
}) {
  const isError = toast.kind === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={`pointer-events-auto flex w-full items-start gap-3 rounded-sm border px-4 py-3 shadow-md ${TONES[toast.kind]}`}
    >
      {toast.kind === 'success' && (
        <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      )}
      {toast.kind === 'error' && (
        <XCircle size={18} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      )}
      {toast.kind === 'info' && (
        <Info size={18} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      )}
      <p className="flex-1 font-serif text-sm leading-relaxed">{toast.message}</p>
      <button
        type="button"
        aria-label={de.toast.closeLabel}
        onClick={() => {
          onDismiss(toast.id);
        }}
        className="-mr-2 -mt-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-sm transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
